// Intervals — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as greedy.js: concept → internals →
// applied templates → errors → complexity → beginner drills → cheatsheet → playground.

import intervalDrills from './intervalDrills';
const drill = id => intervalDrills.find(d => d.id === id);

const intervals = {
  key: 'Intervals',
  tagline: 'Sort by the right key, then sweep once — nearly every interval problem collapses to that single idea.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Intervals',
      type: 'text',
      body: [
        "Intervals problems work on ranges — [start, end] pairs — instead of single values: meeting times, booking windows, ranges of numbers. The core operations are always some combination of checking whether two intervals overlap, merging overlapping ones together, or counting how many are simultaneously active.",
        "Nearly every problem in this pattern reduces to the same two-step recipe: sort the intervals by the RIGHT key (start, or end — the choice matters enormously), then sweep through once, comparing each interval only to its immediate neighbor in the sorted order.",
      ],
    },
    {
      id: 'why',
      title: 'Why the Intervals Pattern?',
      type: 'text',
      body: [
        "Without sorting, checking whether any two intervals in a list overlap requires comparing every pair — O(n²). Sorting first is what collapses that down to a single linear pass: once intervals are ordered by start (or by end), an interval can only possibly overlap the ones immediately next to it in that order — anything farther away is already guaranteed to be out of range.",
        "The recurring decision in this pattern isn't complicated logic — it's picking the correct sort key. Sorting by START is right for merging and for scheduling feasibility; sorting by END is right for the greedy \"maximize how many you can keep\" problems. Using the wrong key silently produces a wrong (or suboptimal) answer, without any error to signal the mistake.",
      ],
    },
    {
      id: 'analogy',
      title: 'How the Intervals Pattern Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Shared Office Calendar, and Choosing Which Movies to Watch',
      body: [
        "Picture a shared office calendar with everyone's meetings listed. To find out if any two meetings clash, the fastest way isn't checking every meeting against every other one — it's sorting them by start time first, then walking down the list once: if a meeting starts before the previous one ends, they clash. Nothing farther down the list needs checking yet, since everything after starts even later.",
        "Now picture choosing which movies to watch in a single day from a list of overlapping showtimes, wanting to watch as MANY movies as possible. The right strategy is to always pick whichever movie ENDS soonest among the remaining options — that leaves the most time free for everything after it. Sorting by end time (not start!) and greedily keeping every movie that doesn't clash with the last one picked is what maximizes the count — this is the classic \"activity selection\" greedy, and it's exactly backwards from the calendar-clash-checking approach, which sorts by start.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing Intervals on a Timeline',
      type: 'interval-diagram',
      body: [
        "Every interval problem is really about bars on a shared timeline. This is the Merge Intervals example: [1,3] and [2,6] overlap (2 falls inside [1,3]), so they merge into one continuous bar, [1,6]. [8,10] and [15,18] don't touch anything, so they stay separate.",
        "Seeing intervals as bars on a line — rather than as pairs of numbers — is what makes \"do these overlap?\" and \"where are the gaps?\" easy to reason about at a glance.",
      ],
      demo: {
        intervals: [[1, 6], [8, 10], [15, 18]],
        min: 0,
        max: 19,
        highlightIndices: [0],
        note: '[1,3] and [2,6] merged into [1,6] — the highlighted bar.',
      },
    },
    {
      id: 'template',
      title: 'The Universal Sort-and-Sweep Recipe',
      type: 'code',
      code:
`/* Two decisions define almost every problem in this pattern:
   1. Sort by START or by END? (get this wrong, and the answer is silently incorrect)
   2. What does "overlap" mean here, and what should happen when it's found?         */
int[][] solveIntervals(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[SORT_KEY] - b[SORT_KEY]);   // SORT_KEY = 0 (start) or 1 (end)

    List<int[]> result = new ArrayList<>();
    for (int[] iv : intervals) {
        if (/* iv relates to the last processed interval in the relevant way */) {
            /* merge, skip, or count, depending on the specific problem */
        } else {
            result.add(iv);
        }
    }
    return result.toArray(new int[0][]);
}`,
      note: 'The overlap test between two intervals [s1,e1] and [s2,e2] is always: s1 < e2 AND s2 < e1. Memorize that formula once — it\'s the building block of every problem in this pattern.',
    },
    {
      id: 'mergetemplate',
      title: 'Applied: Sort by START, Merge Adjacent',
      type: 'code',
      code:
`int[][] merge(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[0] - b[0]);
    List<int[]> merged = new ArrayList<>();
    for (int[] iv : intervals) {
        if (!merged.isEmpty() && iv[0] <= merged.get(merged.size() - 1)[1]) {
            merged.get(merged.size() - 1)[1] = Math.max(merged.get(merged.size() - 1)[1], iv[1]);
        } else {
            merged.add(iv);
        }
    }
    return merged.toArray(new int[0][]);
}`,
      note: 'Sorting by START is the right choice whenever the goal is to combine or check feasibility — Merge Intervals, Insert Interval, Meeting Rooms, and Employee Free Time all use this exact sort key.',
    },
    {
      id: 'greedyendtemplate',
      title: 'Applied: Sort by END, Greedily Keep',
      type: 'code',
      code:
`int eraseOverlapIntervals(int[][] intervals) {
    Arrays.sort(intervals, (a, b) -> a[1] - b[1]);
    int removed = 0;
    int lastEnd = Integer.MIN_VALUE;
    for (int[] iv : intervals) {
        if (iv[0] >= lastEnd) {
            lastEnd = iv[1];   // keep it
        } else {
            removed++;         // conflicts with the last kept interval — remove it
        }
    }
    return removed;
}`,
      note: 'Sorting by END is the right choice whenever the goal is to maximize how many non-overlapping intervals can be KEPT — Non-overlapping Intervals and Minimum Arrows to Burst Balloons both use this exact sort key. This is the "activity selection" greedy, provably optimal via an exchange argument (covered in the Greedy pattern).',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Sorting by the wrong key',
          why: 'Merge Intervals needs intervals sorted by START; Non-overlapping Intervals needs them sorted by END. Using the wrong key for a given problem doesn\'t crash — it silently produces a wrong or suboptimal answer, since the "only adjacent intervals can conflict" guarantee only holds for the correct key.',
          fix: 'Ask explicitly: is this problem about merging/feasibility (sort by start), or about maximizing how many can be kept (sort by end)? Pick the key that matches the actual goal.',
        },
        {
          mistake: 'Getting the overlap test\'s comparison operators backwards',
          why: 'The overlap test s1 < e2 AND s2 < e1 uses strict inequalities — meaning two intervals that only TOUCH at an endpoint (like [1,2] and [2,3]) do NOT count as overlapping. Using ≤ instead of < silently changes which inputs are considered valid.',
          fix: 'Confirm from the problem statement whether touching endpoints count as overlapping or not, and match the comparison operators (< vs ≤) to that rule exactly.',
        },
        {
          mistake: 'Forgetting to update the merged interval\'s end with a max(), not just its own value',
          why: 'When merging [1,10] with [2,3], the merged result must stay [1,10] — but a bug that sets the new end to the incoming interval\'s end unconditionally (mergedEnd = iv[1]) would incorrectly shrink it to [1,3], silently discarding the fact that [1,10] extended further.',
          fix: 'Always take mergedEnd = Math.max(mergedEnd, iv[1]) when merging — never just overwrite it.',
        },
        {
          mistake: 'Re-sorting or rebuilding a list that\'s already guaranteed sorted (Insert Interval)',
          why: 'Insert Interval\'s input is already sorted and non-overlapping — resorting from scratch (as in the brute-force approach) works, but throws away information that lets the optimized single-pass, three-phase approach run in linear instead of linearithmic time.',
          fix: 'Before reaching for a general-purpose merge, check whether the input already carries a guarantee (sorted, non-overlapping) that a more direct single pass can exploit.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Merge Intervals, Insert Interval', time: 'O(n log n)', note: 'Dominated by the initial sort; the merge pass itself is O(n).' },
        { op: 'Non-overlapping Intervals, Min Arrows', time: 'O(n log n)', note: 'Sort by end, then a single O(n) greedy pass.' },
        { op: 'Meeting Rooms (feasibility)', time: 'O(n log n)', note: 'Sort by start, check only adjacent pairs.' },
        { op: 'Meeting Rooms II (min-heap)', time: 'O(n log n)', note: 'Each of n meetings does one O(log n) heap operation.' },
        { op: 'Meeting Rooms II (two-pointer sweep)', time: 'O(n log n)', note: 'Two separate sorts, then a single linear sweep — no heap needed.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for the Intervals pattern when you see…',
      body: [
        'Input given as pairs of [start, end] — meeting times, booking windows, ranges of numbers, or anything else representing a span rather than a single point.',
        'A request to merge overlapping ranges, detect any conflict, or count how many are simultaneously active.',
        'A request to remove the FEWEST intervals (or keep the MOST) so the rest don\'t overlap — a strong signal for the sort-by-end greedy.',
        'Multiple separate lists of intervals that need to be combined or compared against each other (Interval List Intersections, Employee Free Time).',
        'Before coding, explicitly decide: sort by start, or sort by end? — that single decision determines whether the rest of the solution is straightforward or subtly wrong.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('summary-ranges'),
            drill('merge-intervals-drill'),
            drill('interval-list-intersections'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('insert-interval-drill'),
            drill('my-calendar-i'),
            drill('minimum-arrows-burst-balloons'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('employee-free-time'),
            drill('car-pooling'),
            drill('meeting-rooms-ii-drill'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with choosing the right sort key and sweeping once, before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'The overlap test: s1 < e2 AND s2 < e1 — memorize this once, it\'s the building block of every problem here.',
        'Sort by START for merging and feasibility (Merge Intervals, Insert Interval, Meeting Rooms). Sort by END for maximizing how many can be kept (Non-overlapping Intervals, Min Arrows).',
        'After sorting correctly, only ADJACENT intervals in the sorted order can possibly conflict — that\'s what turns an O(n²) all-pairs check into a single O(n) pass.',
        'When merging, always extend the end with Math.max(), never overwrite it — a later interval might end earlier than one already merged in.',
        'A difference array (delta at start, negative delta at end, then prefix-sum) is the tool for "sum of overlapping values at any point" problems like Car Pooling.',
        'Multiple lists of intervals (Employee Free Time) usually reduce to: flatten everything into one list, sort, merge — then the GAPS between merged blocks are the answer.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default intervals;
